import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import * as qs from 'qs';
import { useMemo } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { useLocation, useSearchParams } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { ValidWidths } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { StackedCards } from '@sorare/core/src/components/cards/StackedCards';
import { OpenItemDialogLink } from '@sorare/core/src/components/link/OpenItemDialogLink';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { indexStateToRoute } from '@sorare/core/src/components/search/InstantSearch/utils';
import { ANY_SPORT_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';

import type { StackedToken_anyCard } from './__generated__/index.graphql';

const StyledOpenItemDialogLink = styled(OpenItemDialogLink)`
  width: 100%;
  display: block;
  background-color: transparent;
  position: relative;
`;

export const StackedToken = ({
  card,
  width = 320,
  count,
}: {
  card: StackedToken_anyCard;
  width?: ValidWidths;
  count: number;
}) => {
  const { sport, anyPlayer: player, rarityTyped: rarity } = card;
  const { isIosApp, canUseIosNativeCardPage } = useIsMobileApp();
  const {
    algoliaIndexes,
    so5: { cardMaxGrade },
  } = useConfigContext();
  const location = useLocation();
  const { indexUiState } = useInstantSearch();
  const [searchParams] = useSearchParams();
  const primaryBuyLive = useIsPrimaryBuyLive(card);
  const { language } = useIntlContext();

  const url = useMemo(() => {
    const queryParams = Object.fromEntries(searchParams);
    const currentStateParams: Record<string, unknown> = {
      ...queryParams,
      ...indexStateToRoute(
        indexUiState,
        algoliaIndexes,
        language,
        cardMaxGrade
      ),
    };

    // do not forward some query params to the stack view as they are meaningless
    const notForwardableParamsKey: string[] = [
      SEARCH_PARAMS.SORT,
      SEARCH_PARAMS.PAGE,
      SEARCH_PARAMS.QUERY,
    ];
    const forwardableParams = Object.fromEntries(
      Object.entries(currentStateParams).filter(
        ([key]) => !notForwardableParamsKey.includes(key)
      )
    );

    // store backUrl
    forwardableParams.previousParams = location.search
      ? qs.stringify(currentStateParams, {
          format: 'RFC1738',
          arrayFormat: 'comma',
          encodeValuesOnly: true,
          skipNulls: true,
          addQueryPrefix: true,
        })
      : '';

    return `
      ${generateSportPath(ANY_SPORT_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY, {
        params: {
          playerSlug: player.slug,
          rarity,
        },
        sport,
      })}?${qs.stringify(forwardableParams)}
    `;
  }, [
    algoliaIndexes,
    cardMaxGrade,
    indexUiState,
    language,
    location.search,
    player.slug,
    rarity,
    searchParams,
    sport,
  ]);

  const stackedCards = <StackedCards card={card} width={width} count={count} />;

  if (primaryBuyLive) {
    if (!canUseIosNativeCardPage && isIosApp) return stackedCards;
    return (
      <StyledOpenItemDialogLink item={card} sport={card.sport}>
        {stackedCards}
      </StyledOpenItemDialogLink>
    );
  }
  return <Link to={url}>{stackedCards}</Link>;
};

StackedToken.fragments = {
  anyCard: gql`
    fragment StackedToken_anyCard on AnyCardInterface {
      slug
      sport
      anyPlayer {
        slug
      }
      rarityTyped
      ...useIsPrimaryBuyLive_anyCard
      ...StackedCards_anyCard
    }
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${StackedCards.fragments.anyCard}
  ` as TypedDocumentNode<StackedToken_anyCard>,
};
